(() => {
  const storageKey = 'gc_product_entry_context';
  const maxAgeMs = 5 * 60 * 1000;
  const decoratedLinks = new WeakMap();

  const normalizeDevice = (value) => {
    const device = String(value || '').trim().toLowerCase();
    return device === 'iphone' || device === 'samsung' ? device : '';
  };

  const normalizePath = (value) => {
    const path = String(value || '').trim();
    return path.startsWith('/products/') ? path : '';
  };

  const clear = () => {
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch (error) {
      // Navigation remains functional when sessionStorage is unavailable.
    }
  };

  const store = (urlLike, context = {}) => {
    try {
      const url = new URL(urlLike, window.location.origin);
      const path = normalizePath(url.pathname);
      const device = normalizeDevice(context.device);
      const model = String(context.model || '').trim();
      const finish = String(context.finish || '').trim();

      if (url.origin !== window.location.origin || !path || (!device && !model && !finish)) return false;

      window.sessionStorage.setItem(storageKey, JSON.stringify({
        path,
        device,
        model,
        finish,
        timestamp: Date.now()
      }));
      return true;
    } catch (error) {
      return false;
    }
  };

  const consume = (pathname) => {
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      window.sessionStorage.removeItem(storageKey);
      if (!raw) return null;

      const context = JSON.parse(raw);
      const path = normalizePath(context?.path);
      const timestamp = Number(context?.timestamp);
      if (!path || path !== pathname || !Number.isFinite(timestamp) || Date.now() - timestamp > maxAgeMs) {
        return null;
      }

      const device = normalizeDevice(context.device);
      const model = String(context.model || '').trim();
      const finish = String(context.finish || '').trim();
      return device || model || finish ? { device, model, finish } : null;
    } catch (error) {
      clear();
      return null;
    }
  };

  const storeForLink = (link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const previous = decoratedLinks.get(link);
    const originalHref = previous?.decorated === link.href ? previous.original : link.href;
    const url = new URL(originalHref, window.location.origin);
    if (url.origin !== window.location.origin || !normalizePath(url.pathname)) return;
    // A shared variant/model link is authoritative. Generic product cards inherit
    // the shopper's current model, ahead of a card's representative brand hint.
    if (url.searchParams.has('variant') || url.searchParams.has('model')) {
      clear();
      return;
    }
    let savedModel = '';
    try { savedModel = window.localStorage.getItem('gc_selected_phone_model') || ''; } catch (error) {}
    const model = String(link.dataset.gcModel || savedModel).trim();
    const device = model ? (/samsung|galaxy/i.test(model) ? 'samsung' : /iphone/i.test(model) ? 'iphone' : '') : link.dataset.gcDevice;
    if (model) {
      // Carry the same context to new tabs as well as same-tab navigation.
      url.searchParams.set('model', model);
      if (device) url.searchParams.set('device', device);
      link.href = url.pathname + url.search + url.hash;
      decoratedLinks.set(link, { original: originalHref, decorated: link.href });
    } else if (previous?.decorated === link.href) {
      link.href = originalHref;
      decoratedLinks.delete(link);
    }
    if (!store(link.href, {
      device,
      model
    })) clear();
  };

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element
      ? event.target.closest('a[href*="/products/"]')
      : null;
    if (link) storeForLink(link);
  }, true);

  document.addEventListener('auxclick', (event) => {
    if (event.button !== 1) return;
    const link = event.target instanceof Element
      ? event.target.closest('a[href*="/products/"]')
      : null;
    if (link) storeForLink(link);
  }, true);

  document.addEventListener('gc:saved-phone-change', () => {
    clear();
    document.querySelectorAll('a[href*="/products/"]').forEach(link=>{
      const previous=decoratedLinks.get(link);
      if(previous && previous.decorated===link.href){link.href=previous.original;decoratedLinks.delete(link);}
    });
  });
  window.GoCraftlyProductEntryContext = { consume, store, clear };
})();
